import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  TableSortLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { authGet } from '../../services/apiService';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, ComposedChart, Line } from 'recharts';
import AverageDaysOpenInfo from '../charts/AverageDaysOpenInfo';
import AverageResolutionTimeChart from '../charts/AverageResolutionTimeChart';
import AverageResolutionTimeByCreatedMonthChart from '../charts/AverageResolutionTimeByCreatedMonthChart';

/**
 * BugTrendsView component for displaying production bug trends
 * @returns {JSX.Element} BugTrendsView component
 */
const BugTrendsView = () => {
  // State for ticket details modal
  const [ticketDetailsOpen, setTicketDetailsOpen] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [ticketDetailsTitle, setTicketDetailsTitle] = useState('');
  const [selectedBugs, setSelectedBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trends, setTrends] = useState({
    monthlyTrends: [],
    statusDistribution: [],
    iterationStateDistribution: []
  });
  
  // State for sorting in ticket details modal
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  
  // State for daily bugs data
  const [dailyClosedBugs, setDailyClosedBugs] = useState([]);
  const [loadingDailyClosed, setLoadingDailyClosed] = useState(true);
  const [dailyClosedError, setDailyClosedError] = useState(null);
  
  const [dailyResolvedBugs, setDailyResolvedBugs] = useState([]);
  const [loadingDailyResolved, setLoadingDailyResolved] = useState(true);
  const [dailyResolvedError, setDailyResolvedError] = useState(null);
  
  const [dailyIncomingBugs, setDailyIncomingBugs] = useState([]);
  const [dailyIncomingEnvironments, setDailyIncomingEnvironments] = useState([]);
  const [loadingDailyIncoming, setLoadingDailyIncoming] = useState(true);
  const [dailyIncomingError, setDailyIncomingError] = useState(null);
  
  const [dailyPriorityBugs, setDailyPriorityBugs] = useState([]);
  const [dailyPriorityEnvironments, setDailyPriorityEnvironments] = useState([]);
  const [loadingDailyPriority, setLoadingDailyPriority] = useState(true);
  const [dailyPriorityError, setDailyPriorityError] = useState(null);
  
  // State for resolution time data
  const [resolutionTimeByCreatedMonth, setResolutionTimeByCreatedMonth] = useState([]);
  const [loadingResolutionTimeCreated, setLoadingResolutionTimeCreated] = useState(true);
  const [resolutionTimeCreatedError, setResolutionTimeCreatedError] = useState(null);
  
  const [resolutionTimeByClosedMonth, setResolutionTimeByClosedMonth] = useState([]);
  const [loadingResolutionTimeClosed, setLoadingResolutionTimeClosed] = useState(true);
  const [resolutionTimeClosedError, setResolutionTimeClosedError] = useState(null);
  
  // State for non-closed bugs by month and priority
  const [nonClosedBugsByMonthPriority, setNonClosedBugsByMonthPriority] = useState([]);
  const [loadingNonClosedBugs, setLoadingNonClosedBugs] = useState(true);
  const [nonClosedBugsError, setNonClosedBugsError] = useState(null);
  
  // State for bug phase ages
  const [bugPhaseAges, setBugPhaseAges] = useState([]);
  const [loadingBugPhaseAges, setLoadingBugPhaseAges] = useState(true);
  const [bugPhaseAgesError, setBugPhaseAgesError] = useState(null);

  // State for new charts
  const [dailyP1P2Created, setDailyP1P2Created] = useState([]);
  const [dailyP1P2CreatedEnvironments, setDailyP1P2CreatedEnvironments] = useState([]);
  const [loadingDailyP1P2Created, setLoadingDailyP1P2Created] = useState(true);
  const [dailyP1P2CreatedError, setDailyP1P2CreatedError] = useState(null);

  const [dailyIncomingByEnvironment, setDailyIncomingByEnvironment] = useState([]);
  const [incomingEnvironments, setIncomingEnvironments] = useState([]);
  const [loadingDailyIncomingByEnvironment, setLoadingDailyIncomingByEnvironment] = useState(true);
  const [dailyIncomingByEnvironmentError, setDailyIncomingByEnvironmentError] = useState(null);

  // Function to open ticket details modal
  const openTicketDetails = (title, bugIds, filterOutClosed = false) => {
    setTicketDetailsTitle(title);
    setSelectedTickets(bugIds);
    fetchBugDetails(bugIds, filterOutClosed);
    setTicketDetailsOpen(true);
  };

  // Function to close ticket details modal
  const closeTicketDetails = () => {
    setTicketDetailsOpen(false);
    setSelectedTickets([]);
    setTicketDetailsTitle('');
    setSelectedBugs([]);
  };

  // Function to fetch bug details
  const fetchBugDetails = async (bugIds, filterOutClosed = false) => {
    if (!bugIds || bugIds.length === 0) {
      setSelectedBugs([]);
      return;
    }

    try {
      // Fetch details for each bug ID
      const promises = bugIds.map(id => authGet(`/api/workitems/production-bugs?id=${id}`));
      const responses = await Promise.all(promises);
      
      // Extract bug data from responses
      let bugs = responses
        .filter(response => response.success && response.data && response.data.length > 0)
        .map(response => response.data[0]);
      
      // Filter out closed and removed bugs if requested
      if (filterOutClosed) {
        bugs = bugs.filter(bug => bug.state !== 'Closed' && bug.state !== 'Removed');
      }
      
      setSelectedBugs(bugs);
    } catch (error) {
      console.error('Error fetching bug details:', error);
      setSelectedBugs([]);
    }
  };

  // Fetch bug trends on component mount
  useEffect(() => {
    const fetchBugTrends = async () => {
      try {
        setLoading(true);
        
        // Call the API endpoint to get bug trends
        const response = await authGet('/api/workitems/production-bugs/trends');
        
        if (response.success) {
          // Log the monthly trends data for debugging
          console.log('Monthly trends data:', response.data.monthlyTrends);
          
          // Verify that bug IDs match counts for each month
          if (response.data.monthlyTrends) {
            response.data.monthlyTrends.forEach(month => {
              console.log(`Month: ${month.month}`);
              console.log(`Created: ${month.created}, Created Bug IDs: ${month.createdBugIds?.length || 0}`);
              console.log(`Closed: ${month.closed}, Closed Bug IDs: ${month.closedBugIds?.length || 0}`);
              console.log(`Backlog: ${month.backlog}, Backlog Bug IDs: ${month.backlogBugIds?.length || 0}`);
              
              // Check for discrepancies
              if (month.created !== (month.createdBugIds?.length || 0)) {
                console.warn(`Discrepancy in created bugs for ${month.month}: count=${month.created}, ids=${month.createdBugIds?.length || 0}`);
              }
              if (month.closed !== (month.closedBugIds?.length || 0)) {
                console.warn(`Discrepancy in closed bugs for ${month.month}: count=${month.closed}, ids=${month.closedBugIds?.length || 0}`);
              }
              if (month.backlog !== (month.backlogBugIds?.length || 0)) {
                console.warn(`Discrepancy in backlog bugs for ${month.month}: count=${month.backlog}, ids=${month.backlogBugIds?.length || 0}`);
              }
            });
          }
          
          setTrends(response.data);
        } else {
          throw new Error(response.message || 'Failed to load bug trends');
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching bug trends:', err);
        setError('Failed to load bug trends. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchBugTrends();
  }, []);
  
  // Function to fetch resolution time by created month
  const fetchResolutionTimeByCreatedMonth = async () => {
    try {
      setLoadingResolutionTimeCreated(true);
      
      // Call the API endpoint to get resolution time by created month
      const response = await authGet('/api/workitems/production-bugs/resolution-time-by-created-month');
      
      if (response.success) {
        setResolutionTimeByCreatedMonth(response.data);
      } else {
        throw new Error(response.message || 'Failed to load resolution time by created month');
      }
      
      setResolutionTimeCreatedError(null);
    } catch (err) {
      console.error('Error fetching resolution time by created month:', err);
      setResolutionTimeCreatedError('Failed to load resolution time by created month. Please try again later.');
    } finally {
      setLoadingResolutionTimeCreated(false);
    }
  };
  
  // Function to fetch resolution time by closed month
  const fetchResolutionTimeByClosedMonth = async () => {
    try {
      setLoadingResolutionTimeClosed(true);
      
      // Call the API endpoint to get resolution time by closed month
      const response = await authGet('/api/workitems/production-bugs/resolution-time-by-closed-month');
      
      if (response.success) {
        setResolutionTimeByClosedMonth(response.data);
      } else {
        throw new Error(response.message || 'Failed to load resolution time by closed month');
      }
      
      setResolutionTimeClosedError(null);
    } catch (err) {
      console.error('Error fetching resolution time by closed month:', err);
      setResolutionTimeClosedError('Failed to load resolution time by closed month. Please try again later.');
    } finally {
      setLoadingResolutionTimeClosed(false);
    }
  };
  
  // Fetch daily bugs data on component mount
  useEffect(() => {
    const fetchDailyClosedBugs = async () => {
      try {
        setLoadingDailyClosed(true);
        
        // Call the API endpoint to get daily closed bugs
        const response = await authGet('/api/workitems/production-bugs/daily-closed');
        
        if (response.success) {
          setDailyClosedBugs(response.data);
        } else {
          throw new Error(response.message || 'Failed to load daily closed bugs');
        }
        
        setDailyClosedError(null);
      } catch (err) {
        console.error('Error fetching daily closed bugs:', err);
        setDailyClosedError('Failed to load daily closed bugs. Please try again later.');
      } finally {
        setLoadingDailyClosed(false);
      }
    };
    
    const fetchDailyResolvedBugs = async () => {
      try {
        setLoadingDailyResolved(true);
        
        // Call the API endpoint to get daily resolved bugs
        const response = await authGet('/api/workitems/production-bugs/daily-resolved');
        
        if (response.success) {
          setDailyResolvedBugs(response.data);
        } else {
          throw new Error(response.message || 'Failed to load daily resolved bugs');
        }
        
        setDailyResolvedError(null);
      } catch (err) {
        console.error('Error fetching daily resolved bugs:', err);
        setDailyResolvedError('Failed to load daily resolved bugs. Please try again later.');
      } finally {
        setLoadingDailyResolved(false);
      }
    };
    
    const fetchDailyIncomingBugs = async () => {
      try {
        setLoadingDailyIncoming(true);
        
        // Call the API endpoint to get daily incoming bugs by environment
        const response = await authGet('/api/workitems/production-bugs/daily-incoming');
        
        if (response.success) {
          setDailyIncomingBugs(response.data.dailyIncoming || []);
          setDailyIncomingEnvironments(response.data.environments || []);
        } else {
          throw new Error(response.message || 'Failed to load daily incoming bugs');
        }
        
        setDailyIncomingError(null);
      } catch (err) {
        console.error('Error fetching daily incoming bugs:', err);
        setDailyIncomingError('Failed to load daily incoming bugs. Please try again later.');
      } finally {
        setLoadingDailyIncoming(false);
      }
    };
    
    const fetchDailyPriorityBugs = async () => {
      try {
        setLoadingDailyPriority(true);
        
        // Call the API endpoint to get daily P1+P2 bugs by environment
        const response = await authGet('/api/workitems/production-bugs/daily-priority');
        
        if (response.success) {
          setDailyPriorityBugs(response.data.dailyPriority || []);
          setDailyPriorityEnvironments(response.data.environments || []);
        } else {
          throw new Error(response.message || 'Failed to load daily P1+P2 bugs');
        }
        
        setDailyPriorityError(null);
      } catch (err) {
        console.error('Error fetching daily P1+P2 bugs:', err);
        setDailyPriorityError('Failed to load daily P1+P2 bugs. Please try again later.');
      } finally {
        setLoadingDailyPriority(false);
      }
    };
    
    fetchDailyClosedBugs();
    fetchDailyResolvedBugs();
    fetchDailyIncomingBugs();
    fetchDailyPriorityBugs();
    fetchResolutionTimeByCreatedMonth();
    fetchResolutionTimeByClosedMonth();
    fetchNonClosedBugsByMonthPriority();
    fetchBugPhaseAges();
    fetchDailyP1P2Created();
    fetchDailyIncomingByEnvironment();
  }, []);
  
  // Function to fetch bug phase ages
  const fetchBugPhaseAges = async () => {
    try {
      setLoadingBugPhaseAges(true);
      
      // Call the API endpoint to get bug phase ages
      const response = await authGet('/api/workitems/production-bugs/bug-phase-ages');
      
      if (response.success) {
        setBugPhaseAges(response.data);
      } else {
        throw new Error(response.message || 'Failed to load bug phase ages');
      }
      
      setBugPhaseAgesError(null);
    } catch (err) {
      console.error('Error fetching bug phase ages:', err);
      setBugPhaseAgesError('Failed to load bug phase ages. Please try again later.');
    } finally {
      setLoadingBugPhaseAges(false);
    }
  };
  
  // Function to fetch non-closed bugs by month and priority
  const fetchNonClosedBugsByMonthPriority = async () => {
    try {
      setLoadingNonClosedBugs(true);
      
      // Call the API endpoint to get non-closed bugs by month and priority
      const response = await authGet('/api/workitems/production-bugs/non-closed-by-month-priority');
      
      if (response.success) {
        setNonClosedBugsByMonthPriority(response.data);
      } else {
        throw new Error(response.message || 'Failed to load non-closed bugs by month and priority');
      }
      
      setNonClosedBugsError(null);
    } catch (err) {
      console.error('Error fetching non-closed bugs by month and priority:', err);
      setNonClosedBugsError('Failed to load non-closed bugs by month and priority. Please try again later.');
    } finally {
      setLoadingNonClosedBugs(false);
    }
  };

  // Function to fetch daily P1/P2 created bugs
  const fetchDailyP1P2Created = async () => {
    try {
      setLoadingDailyP1P2Created(true);
      
      // Call the API endpoint to get daily P1/P2 created bugs
      const response = await authGet('/api/workitems/production-bugs/daily-p1-p2-created');
      
      if (response.success) {
        setDailyP1P2Created(response.data);
        setDailyP1P2CreatedEnvironments(response.environments || []);
      } else {
        throw new Error(response.message || 'Failed to load daily P1/P2 created bugs');
      }
      
      setDailyP1P2CreatedError(null);
    } catch (err) {
      console.error('Error fetching daily P1/P2 created bugs:', err);
      setDailyP1P2CreatedError('Failed to load daily P1/P2 created bugs. Please try again later.');
    } finally {
      setLoadingDailyP1P2Created(false);
    }
  };

  // Function to fetch daily incoming bugs by environment
  const fetchDailyIncomingByEnvironment = async () => {
    try {
      setLoadingDailyIncomingByEnvironment(true);
      
      // Call the API endpoint to get daily incoming bugs by environment
      const response = await authGet('/api/workitems/production-bugs/daily-incoming-by-environment');
      
      if (response.success) {
        setDailyIncomingByEnvironment(response.data);
        setIncomingEnvironments(response.environments || []);
      } else {
        throw new Error(response.message || 'Failed to load daily incoming bugs by environment');
      }
      
      setDailyIncomingByEnvironmentError(null);
    } catch (err) {
      console.error('Error fetching daily incoming bugs by environment:', err);
      setDailyIncomingByEnvironmentError('Failed to load daily incoming bugs by environment. Please try again later.');
    } finally {
      setLoadingDailyIncomingByEnvironment(false);
    }
  };

  // Function to render a trend card
  const renderTrendCard = (title, content) => (
    <Card elevation={2} sx={{ height: '100%' }}>
      <CardHeader 
        title={title} 
        titleTypographyProps={{ variant: 'h6' }}
        sx={{ 
          backgroundColor: '#FFF1EC',
          padding: 2,
          color: '#E84E1B'
        }}
      />
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );

  // Function to render monthly created vs closed chart
  const renderMonthlyCreatedVsClosed = () => {
    const { monthlyTrends } = trends;
    
    // Function to get all not closed bug IDs
    const getAllNotClosedBugIds = () => {
      const notClosedBugIds = new Set();
      
      // Collect all backlog bug IDs from each month
      monthlyTrends.forEach(month => {
        if (month.backlogBugIds && Array.isArray(month.backlogBugIds)) {
          month.backlogBugIds.forEach(id => notClosedBugIds.add(id));
        }
      });
      
      return Array.from(notClosedBugIds);
    };
    
    return (
      <Box>
        <Typography variant="h6" align="center" gutterBottom>
          Monthly Bug Trends: Opened vs Closed Bugs
        </Typography>
        <Typography variant="caption" align="center" display="block" gutterBottom>
          This chart uses real data from the database. Click on bars or points to see the actual tickets.
        </Typography>
        
        {/* Add link to view all not closed bugs */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<InfoIcon />}
            onClick={() => {
              const notClosedBugIds = getAllNotClosedBugIds();
              const latestMonth = monthlyTrends[monthlyTrends.length - 1];
              const title = `All Not Closed Bugs (Total: ${latestMonth?.backlog || 0})`;
              openTicketDetails(title, notClosedBugIds, true); // Pass true to filter out closed bugs
            }}
            sx={{ 
              textTransform: 'none',
              borderColor: '#4BACC6',
              color: '#4BACC6',
              '&:hover': {
                borderColor: '#3A8BA5',
                backgroundColor: 'rgba(75, 172, 198, 0.08)'
              }
            }}
          >
            View All Not Closed Bugs
          </Button>
        </Box>
        
        <Box sx={{ height: 500, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={monthlyTrends}
              margin={{
                top: 20, right: 30, left: 20, bottom: 30,
              }}
            onClick={(data) => {
              // Check if we have valid click data
              if (!data || !data.activePayload || data.activePayload.length === 0) {
                return;
              }
              
              // Get the clicked data point and data key
              const clickedData = data.activePayload[0].payload;
              const clickedDataKey = data.activePayload[0].dataKey;
              
              console.log('Clicked on chart:', clickedDataKey, clickedData);
              
              let title = '';
              let bugIds = [];
              
              // Determine which data series was clicked and get the corresponding bug IDs
              if (clickedDataKey === 'created') {
                title = `Opened Bugs - ${clickedData.month}`;
                bugIds = clickedData.createdBugIds || [];
                console.log(`Opening ${bugIds.length} created bugs for ${clickedData.month}`);
              } else if (clickedDataKey === 'closed') {
                title = `Closed Bugs - ${clickedData.month}`;
                bugIds = clickedData.closedBugIds || [];
                console.log(`Opening ${bugIds.length} closed bugs for ${clickedData.month}`);
              } else if (clickedDataKey === 'backlog') {
                title = `Backlog Bugs - ${clickedData.month}`;
                bugIds = clickedData.backlogBugIds || [];
                console.log(`Opening ${bugIds.length} backlog bugs for ${clickedData.month}`);
              }
              
              // Verify that the bug IDs match the count shown in the chart
              const count = clickedData[clickedDataKey];
              if (bugIds.length !== count) {
                console.warn(`Bug ID count (${bugIds.length}) doesn't match displayed count (${count}) for ${clickedDataKey} in ${clickedData.month}`);
              }
              
              // Open the ticket details modal if we have bug IDs
              if (bugIds && bugIds.length > 0) {
                openTicketDetails(title, bugIds);
              } else {
                console.warn(`No bug IDs found for ${clickedDataKey} in ${clickedData.month}`);
              }
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              angle={-45} 
              textAnchor="end" 
              height={70} 
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              yAxisId="left" 
              orientation="left" 
              label={{ value: 'Number of Bugs', angle: -90, position: 'insideLeft' }}
            />
            <YAxis 
              yAxisId="right" 
              orientation="right" 
              label={{ value: 'Backlog', angle: 90, position: 'insideRight' }}
            />
            <RechartsTooltip 
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  const dataKey = payload[0].dataKey;
                  const value = payload[0].value;
                  
                  let bugIds = [];
                  if (dataKey === 'created') {
                    bugIds = data.createdBugIds || [];
                  } else if (dataKey === 'closed') {
                    bugIds = data.closedBugIds || [];
                  } else if (dataKey === 'backlog') {
                    bugIds = data.backlogBugIds || [];
                  }
                  
                  // Use the actual value from the chart for the count
                  const displayCount = value;
                  const availableCount = bugIds.length;
                  
                  return (
                    <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc' }}>
                      <p style={{ fontWeight: 'bold' }}>{`${payload[0].name}: ${displayCount}`}</p>
                      <p style={{ fontSize: '12px', color: '#666' }}>
                        Click to view {availableCount} ticket{availableCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend 
              verticalAlign="bottom" 
              height={36}
              wrapperStyle={{ paddingTop: '20px' }}
            />
            <Bar 
              yAxisId="left" 
              dataKey="created" 
              name="Opened Bugs" 
              fill="#C0504D" 
              barSize={20}
              label={{ 
                position: 'top', 
                fill: '#000000', 
                fontSize: 10 
              }}
            />
            <Bar 
              yAxisId="left" 
              dataKey="closed" 
              name="Closed Bugs" 
              fill="#00B050" 
              barSize={20}
              label={{ 
                position: 'top', 
                fill: '#000000', 
                fontSize: 10 
              }}
            />
            <Line 
              yAxisId="right" 
              type="monotone" 
              dataKey="backlog" 
              name="Backlog" 
              stroke="#4BACC6" 
              strokeWidth={2}
              dot={{ r: 4 }}
              label={{ 
                position: 'top', 
                fill: '#4BACC6', 
                fontSize: 10 
              }}
            />
          </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    );
  };

  // Function to render status distribution table
  const renderStatusDistribution = () => {
    const { statusDistribution } = trends;
    
    // Calculate total count
    const totalCount = statusDistribution.reduce((sum, item) => sum + item.count, 0);
    
    return (
      <Box>
        <Typography variant="body1" paragraph>
          Active Bugs - by Status
        </Typography>
        <Typography variant="caption" paragraph>
          This table uses real data from the database. Click the info icon to view the actual tickets.
        </Typography>
        
        <TableContainer component={Paper}>
          <Table stickyHeader aria-label="status distribution table">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell align="right">Count</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {statusDistribution.map((row) => (
                <TableRow 
                  key={row.status}
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    backgroundColor: `${row.color}20` // Add transparency to the color
                  }}
                >
                  <TableCell component="th" scope="row">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box 
                        sx={{ 
                          width: 16, 
                          height: 16, 
                          backgroundColor: row.color,
                          marginRight: 1,
                          borderRadius: '50%'
                        }} 
                      />
                      {row.status}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{row.count}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Tickets">
                      <IconButton 
                        size="small" 
                        onClick={() => openTicketDetails(`${row.status} Bugs`, row.bugIds || [])}
                        disabled={!row.bugIds || row.bugIds.length === 0}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {/* Add a totals row */}
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell component="th" scope="row">
                  <strong>Total</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>{totalCount}</strong>
                </TableCell>
                <TableCell align="center">
                  {/* No action for total row */}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Function to render iteration bugs table (using iterationPath)
  const renderIterationStatePivot = () => {
    const { iterationStateDistribution, statusDistribution } = trends;
    
    // Get the total from status distribution to ensure consistency
    const statusTotalCount = statusDistribution.reduce((sum, item) => sum + item.count, 0);
    
    // Calculate total count per iteration
    const iterationCounts = [];
    let calculatedTotal = 0;
    
    // Check if we have iteration data
    if (!iterationStateDistribution || iterationStateDistribution.length === 0) {
      console.warn('No iteration state distribution data available');
      
      // Return empty table with message
      return (
        <Box>
          <Typography variant="body1" paragraph>
            Active Bugs - Sprint
          </Typography>
          <Typography variant="caption" paragraph>
            No sprint data available. This could be because there are no active bugs with assigned sprints.
          </Typography>
          
          <TableContainer component={Paper}>
            <Table stickyHeader aria-label="iteration bugs table">
              <TableHead>
                <TableRow>
                  <TableCell>Sprint</TableCell>
                  <TableCell align="right">Count</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={3} align="center">No data available</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      );
    }
    
    console.log('Iteration state distribution:', iterationStateDistribution);
    
    // Process all iterations from the data
    iterationStateDistribution.forEach(item => {
      // Get the original iteration path
      const fullIterationPath = item.iteration;
      
      // Split by backslash and take the last part
      const pathParts = fullIterationPath.split('\\');
      const iteration = pathParts.length > 0 ? pathParts[pathParts.length - 1] : fullIterationPath;
      
      let iterationTotal = 0;
      
      // Sum all states for this iteration, excluding non-numeric properties
      Object.keys(item).forEach(key => {
        if (key !== 'iteration' && key !== 'bugIds' && typeof item[key] === 'number') {
          iterationTotal += item[key];
        }
      });
      
      if (iterationTotal > 0) {
        // Check if we already have this iteration in our list (after splitting)
        const existingIndex = iterationCounts.findIndex(i => i.iteration === iteration);
        
        if (existingIndex >= 0) {
          // Add to existing iteration count
          iterationCounts[existingIndex].count += iterationTotal;
          // Store the original item reference for accessing bug IDs later
          if (!iterationCounts[existingIndex].originalItems) {
            iterationCounts[existingIndex].originalItems = [];
          }
          iterationCounts[existingIndex].originalItems.push(item);
        } else {
          // Add as new iteration
          iterationCounts.push({
            iteration,
            count: iterationTotal,
            originalItems: [item] // Store the original item reference for accessing bug IDs later
          });
        }
        
        calculatedTotal += iterationTotal;
      }
    });
    
    // Check if we're missing any bugs in our iteration counts
    const missingCount = statusTotalCount - calculatedTotal;
    
    // If there are missing bugs, add an "Other" category
    if (missingCount > 0) {
      iterationCounts.push({
        iteration: "Other/Unassigned",
        count: missingCount
      });
    }
    
    // Sort by count (descending)
    iterationCounts.sort((a, b) => b.count - a.count);
    
    // Use the same total as status distribution
    const totalCount = statusTotalCount;
    
    return (
      <Box>
        <Typography variant="body1" paragraph>
          Active Bugs - Sprint
        </Typography>
        <Typography variant="caption" paragraph>
          This table shows bugs grouped by sprint (from iteration path). Click the info icon to view the actual tickets.
        </Typography>
        
        <TableContainer component={Paper}>
          <Table stickyHeader aria-label="iteration bugs table">
            <TableHead>
              <TableRow>
                <TableCell>Sprint</TableCell>
                <TableCell align="right">Count</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {iterationCounts.map((row) => (
                <TableRow 
                  key={row.iteration}
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    backgroundColor: '#f8f8f8'
                  }}
                >
                  <TableCell component="th" scope="row">
                    {row.iteration}
                  </TableCell>
                  <TableCell align="right">{row.count}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Tickets">
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          if (row.originalItems && row.originalItems.length > 0) {
                            // Collect all bug IDs from all states for this iteration
                            const bugIds = [];
                            
                            row.originalItems.forEach(iterationData => {
                              if (iterationData.bugIds) {
                                Object.keys(iterationData.bugIds).forEach(state => {
                                  if (iterationData.bugIds[state] && Array.isArray(iterationData.bugIds[state])) {
                                    bugIds.push(...iterationData.bugIds[state]);
                                  }
                                });
                              }
                            });
                            
                            // Remove duplicates
                            const uniqueBugIds = [...new Set(bugIds)];
                            openTicketDetails(`${row.iteration} Bugs`, uniqueBugIds);
                          } else if (row.iteration === "Other/Unassigned") {
                            // Handle the "Other/Unassigned" category
                            // This would require fetching all bugs without an iteration path
                            // For now, just show a message
                            openTicketDetails(`${row.iteration} Bugs`, []);
                          }
                        }}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {/* Add a totals row */}
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell component="th" scope="row">
                  <strong>Total</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>{totalCount}</strong>
                </TableCell>
                <TableCell align="center">
                  {/* No action for total row */}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };
  
  // State for fix version data
  const [fixVersionData, setFixVersionData] = useState([]);
  const [loadingFixVersion, setLoadingFixVersion] = useState(true);
  const [fixVersionError, setFixVersionError] = useState(null);
  
  // Fetch fix version data on component mount
  useEffect(() => {
    const fetchFixVersionData = async () => {
      try {
        setLoadingFixVersion(true);
        
        // Fetch all active bugs to get their fixVersionDummy values
        const response = await authGet('/api/workitems/production-bugs?state=Active&state=New&state=In%20Progress&state=Ready%20for%20Test&state=Ready%20for%20Review&state=Blocked&state=Deferred&state=Approved&state=Reopened&state=Review&state=Clarification%20Required');
        
        if (!response.success || !response.data || response.data.length === 0) {
          setFixVersionData([]);
          return;
        }
        
        const activeBugs = response.data;
        console.log(`Fetched ${activeBugs.length} active bugs for fix version analysis`);
        
        // Group bugs by fixVersionDummy
        const fixVersionCounts = {};
        
        activeBugs.forEach(bug => {
          const fixVersion = bug.fixVersionDummy || 'Unassigned';
          
          if (!fixVersionCounts[fixVersion]) {
            fixVersionCounts[fixVersion] = {
              count: 0,
              bugIds: []
            };
          }
          
          fixVersionCounts[fixVersion].count++;
          fixVersionCounts[fixVersion].bugIds.push(bug.id);
        });
        
        // Convert to array for rendering
        const fixVersionArray = Object.keys(fixVersionCounts).map(version => ({
          fixVersion: version,
          count: fixVersionCounts[version].count,
          bugIds: fixVersionCounts[version].bugIds
        }));
        
        // Sort by count (descending)
        fixVersionArray.sort((a, b) => b.count - a.count);
        
        setFixVersionData(fixVersionArray);
        setFixVersionError(null);
      } catch (error) {
        console.error('Error fetching fix version data:', error);
        setFixVersionError('Failed to load fix version data. Please try again later.');
        setFixVersionData([]);
      } finally {
        setLoadingFixVersion(false);
      }
    };
    
    fetchFixVersionData();
  }, []);
  
  // Function to render fix version bugs table (using fixVersionDummy)
  const renderFixVersionPivot = () => {
    if (loadingFixVersion) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (fixVersionError) {
      return (
        <Box>
          <Typography color="error">{fixVersionError}</Typography>
        </Box>
      );
    }
    
    // If we don't have any data, show a message
    if (!fixVersionData || fixVersionData.length === 0) {
      return renderEmptyFixVersionTable();
    }
    
    // Calculate total
    const totalCount = fixVersionData.reduce((sum, item) => sum + item.count, 0);
    
    return (
      <Box>
        <Typography variant="body1" paragraph>
          Active Bugs - by Fix Version
        </Typography>
        <Typography variant="caption" paragraph>
          This table shows bugs grouped by fix version (from fixVersionDummy field). Click the info icon to view the actual tickets.
        </Typography>
        
        <TableContainer component={Paper}>
          <Table stickyHeader aria-label="fix version bugs table">
            <TableHead>
              <TableRow>
                <TableCell>Fix Version</TableCell>
                <TableCell align="right">Count</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fixVersionData.map((row) => (
                <TableRow 
                  key={row.fixVersion}
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    backgroundColor: '#f8f8f8'
                  }}
                >
                  <TableCell component="th" scope="row">
                    {row.fixVersion}
                  </TableCell>
                  <TableCell align="right">{row.count}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Tickets">
                      <IconButton 
                        size="small" 
                        onClick={() => openTicketDetails(`${row.fixVersion} Bugs`, row.bugIds)}
                        disabled={!row.bugIds || row.bugIds.length === 0}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {/* Add a totals row */}
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell component="th" scope="row">
                  <strong>Total</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>{totalCount}</strong>
                </TableCell>
                <TableCell align="center">
                  {/* No action for total row */}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };
  
  // Helper function to render empty fix version table
  const renderEmptyFixVersionTable = () => {
    return (
      <Box>
        <Typography variant="body1" paragraph>
          Active Bugs - by Fix Version
        </Typography>
        <Typography variant="caption" paragraph>
          No fix version data available. This could be because there are no active bugs with assigned fix versions.
        </Typography>
        
        <TableContainer component={Paper}>
          <Table stickyHeader aria-label="fix version bugs table">
            <TableHead>
              <TableRow>
                <TableCell>Fix Version</TableCell>
                <TableCell align="right">Count</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={3} align="center">No data available</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper elevation={2} style={{ padding: 16, marginTop: 16 }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }

  // Function to generate trend insights
  const renderTrendInsights = () => {
    const { monthlyTrends } = trends;
    
    // Need at least 3 months of data to generate insights
    if (!monthlyTrends || monthlyTrends.length < 3) {
      return (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Insufficient Data</AlertTitle>
          At least 3 months of data are required to generate trend insights.
        </Alert>
      );
    }
    
    // Get current date to determine which months are completed
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonthIndex = currentDate.getMonth(); // 0-based (0 = January, 11 = December)
    
    // Filter out the current ongoing month from the analysis
    const completedMonths = monthlyTrends.filter(monthData => {
      // Parse the month string (e.g., "Jan 2025" -> year: 2025, month: 0)
      const [monthName, yearStr] = monthData.month.split(' ');
      const year = parseInt(yearStr);
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIndex = monthNames.indexOf(monthName);
      
      // Exclude current month if it's ongoing
      if (year === currentYear && monthIndex === currentMonthIndex) {
        return false;
      }
      
      return true;
    });
    
    // Need at least 2 completed months for comparison
    if (completedMonths.length < 2) {
      return (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Insufficient Completed Months</AlertTitle>
          At least 2 completed months are required to generate trend insights. Current month is excluded from analysis.
        </Alert>
      );
    }
    
    // Get the last 2 completed months for comparison
    const lastTwoCompletedMonths = completedMonths.slice(-2);
    const currentMonth = lastTwoCompletedMonths[1]; // Most recent completed month
    const previousMonth = lastTwoCompletedMonths[0]; // Previous completed month
    
    // Calculate month-over-month changes
    const createdMoM = currentMonth.created - previousMonth.created;
    const createdMoMPercent = previousMonth.created ? Math.round((createdMoM / previousMonth.created) * 100) : 0;
    
    const closedMoM = currentMonth.closed - previousMonth.closed;
    const closedMoMPercent = previousMonth.closed ? Math.round((closedMoM / previousMonth.closed) * 100) : 0;
    
    const backlogMoM = currentMonth.backlog - previousMonth.backlog;
    const backlogMoMPercent = previousMonth.backlog ? Math.round((backlogMoM / previousMonth.backlog) * 100) : 0;
    
    // For simplicity, we'll focus on month-over-month comparison since we only have 2 completed months
    // Remove quarter-over-quarter analysis as it requires more historical data
    
    // Determine severity and message
    let severity = 'info';
    let title = 'Bug Trend Analysis';
    let messages = [];
    
    // Month-over-month insights
    if (createdMoM > 0) {
      messages.push(`📈 Bug creation increased by ${createdMoM} (${createdMoMPercent}%) compared to last month.`);
      if (createdMoM > closedMoM) {
        severity = 'warning';
      }
    } else if (createdMoM < 0) {
      messages.push(`📉 Bug creation decreased by ${Math.abs(createdMoM)} (${Math.abs(createdMoMPercent)}%) compared to last month.`);
    } else {
      messages.push(`📊 Bug creation remained stable compared to last month.`);
    }
    
    if (closedMoM > 0) {
      messages.push(`✅ Bug resolution increased by ${closedMoM} (${closedMoMPercent}%) compared to last month.`);
      if (closedMoM > createdMoM) {
        severity = 'success';
      }
    } else if (closedMoM < 0) {
      messages.push(`⚠️ Bug resolution decreased by ${Math.abs(closedMoM)} (${Math.abs(closedMoMPercent)}%) compared to last month.`);
      severity = 'warning';
    } else {
      messages.push(`📊 Bug resolution remained stable compared to last month.`);
    }
    
    if (backlogMoM > 0) {
      messages.push(`⚠️ Backlog increased by ${backlogMoM} (${backlogMoMPercent}%) compared to last month.`);
      severity = 'warning';
    } else if (backlogMoM < 0) {
      messages.push(`✅ Backlog decreased by ${Math.abs(backlogMoM)} (${Math.abs(backlogMoMPercent)}%) compared to last month.`);
      severity = 'success';
    } else {
      messages.push(`📊 Backlog remained stable compared to last month.`);
    }
    
    // Overall trend assessment
    if (createdMoM > closedMoM) {
      messages.push(`⚠️ Bug creation is outpacing resolution - backlog may continue to grow.`);
    } else if (closedMoM > createdMoM) {
      messages.push(`✅ Bug resolution is outpacing creation - backlog is being reduced.`);
    } else {
      messages.push(`📊 Bug creation and resolution are balanced.`);
    }
    
    return (
      <Alert severity={severity} sx={{ mb: 3 }}>
        <AlertTitle sx={{ fontSize: '1.3rem', fontWeight: 'bold' }}>{title}</AlertTitle>
        {messages.map((message, index) => (
          <Typography key={index} variant="body1" sx={{ mb: 1, fontSize: '1.1rem' }}>
            {message}
          </Typography>
        ))}
      </Alert>
    );
  };

  // Function to render daily incoming bugs chart
  const renderDailyIncomingBugs = () => {
    if (loadingDailyIncoming) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (dailyIncomingError) {
      return (
        <Box>
          <Typography color="error">{dailyIncomingError}</Typography>
        </Box>
      );
    }
    
    // Filter out weekends if needed
    const filteredData = dailyIncomingBugs.filter(item => {
      const date = new Date(item.date);
      return date.getDay() !== 0 && date.getDay() !== 6; // Skip weekends (0 = Sunday, 6 = Saturday)
    });
    
    // Calculate total bugs
    const totalBugs = filteredData.reduce((sum, item) => sum + (item.total || 0), 0);
    
    // Environment colors for consistent visualization
    const environmentColors = {
      'Legacy': '#0070C0',           // Blue
      'Modernization': '#00B050',    // Green
      'Legacy and Modernization': '#FFC000', // Amber
      'Unknown': '#95A5A6'           // Grey
    };
    
    // Generate colors for environments
    const getEnvironmentColor = (environment, index) => {
      if (environmentColors[environment]) {
        return environmentColors[environment];
      }
      
      // Generate a color based on index if not predefined
      const colors = [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', 
        '#DDA0DD', '#98D8C8', '#F39C12', '#E74C3C', '#9B59B6',
        '#3498DB', '#1ABC9C', '#2ECC71', '#F1C40F', '#E67E22'
      ];
      return colors[index % colors.length];
    };
    
    return (
      <Box>
        <Typography variant="body1" paragraph>
          Daily incoming bugs for the last 60 days (excluding weekends), stacked by environment.
        </Typography>
        <Typography variant="subtitle1" paragraph fontWeight="bold" color="primary">
          Total Bugs: {totalBugs}
        </Typography>
        <Typography variant="caption" paragraph>
          This chart uses real data from the database and shows bugs grouped by environment.
        </Typography>
        
        <Box sx={{ height: 400, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={filteredData}
              margin={{
                top: 20, right: 30, left: 20, bottom: 70,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                angle={-45} 
                textAnchor="end" 
                height={70} 
                tick={{ fontSize: 10 }}
              />
              <YAxis 
                label={{ value: 'Number of Bugs', angle: -90, position: 'insideLeft' }}
              />
              <RechartsTooltip 
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    // Calculate total for this day
                    const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
                    
                    return (
                      <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc' }}>
                        <p style={{ fontWeight: 'bold' }}>{`Date: ${label}`}</p>
                        {payload.map((entry, index) => (
                          <p key={index} style={{ color: entry.color }}>
                            {`${entry.dataKey}: ${entry.value} bugs`}
                          </p>
                        ))}
                        <p style={{ fontWeight: 'bold' }}>{`Total: ${total} bugs`}</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend verticalAlign="top" height={36} />
              
              {/* Render stacked bars for each environment */}
              {dailyIncomingEnvironments.map((environment, index) => (
                <Bar 
                  key={environment}
                  dataKey={environment} 
                  name={environment} 
                  stackId="a"
                  fill={getEnvironmentColor(environment, index)}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    );
  };
  
  // Function to render daily resolved bugs chart
  const renderDailyResolvedBugs = () => {
    if (loadingDailyResolved) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (dailyResolvedError) {
      return (
        <Box>
          <Typography color="error">{dailyResolvedError}</Typography>
        </Box>
      );
    }
    
    // Filter out weekends if needed
    const filteredData = dailyResolvedBugs.filter(item => {
      const date = new Date(item.date);
      return date.getDay() !== 0 && date.getDay() !== 6; // Skip weekends (0 = Sunday, 6 = Saturday)
    });
    
    return (
      <Box>
        <Typography variant="body1" paragraph>
          Daily resolved bugs for the last 60 days (excluding weekends).
        </Typography>
        <Typography variant="caption" paragraph>
          This chart shows bugs moved to the Resolved state each day.
        </Typography>
        
        <Box sx={{ height: 400, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={filteredData}
              margin={{
                top: 20, right: 30, left: 20, bottom: 70,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                angle={-45} 
                textAnchor="end" 
                height={70} 
                tick={{ fontSize: 10 }}
              />
              <YAxis 
                label={{ value: 'Number of Bugs', angle: -90, position: 'insideLeft' }}
              />
              <RechartsTooltip />
              <Legend verticalAlign="top" height={36} />
              <Bar 
                dataKey="count" 
                name="Resolved Bugs" 
                fill="#FFA500" 
                label={{ 
                  position: 'top', 
                  fill: '#000000', 
                  fontSize: 10,
                  formatter: (value) => value > 0 ? value : ''
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    );
  };
  
  // Function to render daily closed bugs chart
  const renderDailyClosedBugs = () => {
    if (loadingDailyClosed) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (dailyClosedError) {
      return (
        <Box>
          <Typography color="error">{dailyClosedError}</Typography>
        </Box>
      );
    }
    
    // Filter out weekends if needed
    const filteredData = dailyClosedBugs.filter(item => {
      const date = new Date(item.date);
      return date.getDay() !== 0 && date.getDay() !== 6; // Skip weekends (0 = Sunday, 6 = Saturday)
    });
    
    return (
      <Box>
        <Typography variant="body1" paragraph>
          Daily closed bugs for the last 60 days (excluding weekends).
        </Typography>
        <Typography variant="caption" paragraph>
          This chart uses real data from the database.
        </Typography>
        
        
        
        
        <Box sx={{ height: 400, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={filteredData}
              margin={{
                top: 20, right: 30, left: 20, bottom: 70,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                angle={-45} 
                textAnchor="end" 
                height={70} 
                tick={{ fontSize: 10 }}
              />
              <YAxis 
                label={{ value: 'Number of Bugs', angle: -90, position: 'insideLeft' }}
              />
              <RechartsTooltip />
              <Legend verticalAlign="top" height={36} />
              <Bar 
                dataKey="count" 
                name="Closed Bugs" 
                fill="#00B050" 
                label={{ 
                  position: 'top', 
                  fill: '#000000', 
                  fontSize: 10,
                  formatter: (value) => value > 0 ? value : ''
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    );
  };
  
  
  // Function to render resolution time by created month chart
  const renderResolutionTimeByCreatedMonth = () => {
    if (loadingResolutionTimeCreated) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (resolutionTimeCreatedError) {
      return (
        <Box>
          <Typography color="error">{resolutionTimeCreatedError}</Typography>
        </Box>
      );
    }
    
    return (
      <Box>
        <Typography variant="body1" paragraph>
          Average resolution time (in days) for issues by created month.
        </Typography>
        <Typography variant="caption" paragraph>
          Only includes issues in completed, closed, and done states.
        </Typography>
        
        <Box sx={{ height: 400, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={resolutionTimeByCreatedMonth}
              margin={{
                top: 20, right: 30, left: 20, bottom: 70,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                angle={-45} 
                textAnchor="end" 
                height={70} 
                tick={{ fontSize: 10 }}
              />
              <YAxis 
                label={{ value: 'Average Days', angle: -90, position: 'insideLeft' }}
              />
              <RechartsTooltip 
                formatter={(value, name) => [`${value} days`, 'Avg. Resolution Time']}
                labelFormatter={(label) => `Created: ${label}`}
              />
              <Legend verticalAlign="top" height={36} />
              <Bar 
                dataKey="avgResolutionDays" 
                name="Avg. Resolution Time" 
                fill="#8884d8" 
                label={{ 
                  position: 'top', 
                  fill: '#000000', 
                  fontSize: 10,
                  formatter: (value) => `${value} days`
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    );
  };
  
  // Function to render resolution time by closed month chart
  const renderResolutionTimeByClosedMonth = () => {
    if (loadingResolutionTimeClosed) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (resolutionTimeClosedError) {
      return (
        <Box>
          <Typography color="error">{resolutionTimeClosedError}</Typography>
        </Box>
      );
    }
    
    return (
      <Box>
        <Typography variant="body1" paragraph>
          Average resolution time (in days) for issues by closed month.
        </Typography>
        <Typography variant="caption" paragraph>
          Only includes issues in completed, closed, and done states.
        </Typography>
        
        <Box sx={{ height: 400, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={resolutionTimeByClosedMonth}
              margin={{
                top: 20, right: 30, left: 20, bottom: 70,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                angle={-45} 
                textAnchor="end" 
                height={70} 
                tick={{ fontSize: 10 }}
              />
              <YAxis 
                label={{ value: 'Average Days', angle: -90, position: 'insideLeft' }}
              />
              <RechartsTooltip 
                formatter={(value, name) => [`${value} days`, 'Avg. Resolution Time']}
                labelFormatter={(label) => `Closed: ${label}`}
              />
              <Legend verticalAlign="top" height={36} />
              <Bar 
                dataKey="avgResolutionDays" 
                name="Avg. Resolution Time" 
                fill="#82ca9d" 
                label={{ 
                  position: 'top', 
                  fill: '#000000', 
                  fontSize: 10,
                  formatter: (value) => `${value} days`
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    );
  };
  
  // Function to render non-closed bugs by month and priority chart
  const renderNonClosedBugsByMonthPriority = () => {
    if (loadingNonClosedBugs) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (nonClosedBugsError) {
      return (
        <Box>
          <Typography color="error">{nonClosedBugsError}</Typography>
        </Box>
      );
    }
    
    return (
      <Box>
        <Typography variant="body1" paragraph>
          Non-closed bugs by month of creation and priority.
        </Typography>
        <Typography variant="caption" paragraph>
          This chart shows all active bugs grouped by month of creation and stacked by priority.
          Click on bars to see the actual tickets.
        </Typography>
        
        <Box sx={{ height: 400, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={nonClosedBugsByMonthPriority}
              margin={{
                top: 20, right: 30, left: 20, bottom: 70,
              }}
              onClick={(data) => {
                // Check if we have valid click data
                if (!data || !data.activePayload || data.activePayload.length === 0) {
                  return;
                }
                
                // Get the clicked data point and data key
                const clickedData = data.activePayload[0].payload;
                const clickedDataKey = data.activePayload[0].dataKey;
                
                console.log('Clicked on chart:', clickedDataKey, clickedData);
                
                // Only process if it's a priority key (P1, P2, P3, P4)
                if (['P1', 'P2', 'P3', 'P4'].includes(clickedDataKey)) {
                  const title = `${clickedDataKey} Bugs - ${clickedData.month}`;
                  const bugIds = clickedData.bugIds[clickedDataKey] || [];
                  
                  console.log(`Opening ${bugIds.length} ${clickedDataKey} bugs for ${clickedData.month}`);
                  
                  // Open the ticket details modal if we have bug IDs
                  if (bugIds && bugIds.length > 0) {
                    openTicketDetails(title, bugIds);
                  } else {
                    console.warn(`No bug IDs found for ${clickedDataKey} in ${clickedData.month}`);
                  }
                }
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                angle={-45} 
                textAnchor="end" 
                height={70} 
                tick={{ fontSize: 10 }}
              />
              <YAxis 
                label={{ value: 'Number of Bugs', angle: -90, position: 'insideLeft' }}
              />
              <RechartsTooltip 
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    // Calculate total for this month
                    const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
                    
                    return (
                      <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc' }}>
                        <p style={{ fontWeight: 'bold' }}>{`Month: ${label}`}</p>
                        {payload.map((entry, index) => (
                          <p key={index} style={{ color: entry.color }}>
                            {`${entry.name}: ${entry.value} bugs`}
                          </p>
                        ))}
                        <p style={{ fontWeight: 'bold' }}>{`Total: ${total} bugs`}</p>
                        <p style={{ fontSize: '12px', color: '#666' }}>
                          Click to view tickets
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend verticalAlign="top" height={36} />
              <Bar 
                dataKey="P1" 
                name="P1 Bugs" 
                stackId="a" 
                fill="#FF0000" 
              />
              <Bar 
                dataKey="P2" 
                name="P2 Bugs" 
                stackId="a" 
                fill="#FFA500" 
              />
              <Bar 
                dataKey="P3" 
                name="P3 Bugs" 
                stackId="a" 
                fill="#00B050" 
              />
              <Bar 
                dataKey="P4" 
                name="P4 Bugs" 
                stackId="a" 
                fill="#0070C0" 
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    );
  };

  // Function to render bug phase ages chart
  const renderBugPhaseAges = () => {
    if (loadingBugPhaseAges) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (bugPhaseAgesError) {
      return (
        <Box>
          <Typography color="error">{bugPhaseAgesError}</Typography>
        </Box>
      );
    }
    
    return (
      <Box>
        <Typography variant="body1" paragraph>
          Average Bug Age by Phase (in days)
        </Typography>
        <Typography variant="caption" paragraph>
          This chart shows the average time bugs spend in development (creation to resolution) and QA (resolution to closure) phases.
        </Typography>
        
        <Box sx={{ height: 400, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={bugPhaseAges}
              margin={{
                top: 20, right: 30, left: 20, bottom: 70,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                angle={-45} 
                textAnchor="end" 
                height={70} 
                tick={{ fontSize: 10 }}
              />
              <YAxis 
                label={{ value: 'Average Days', angle: -90, position: 'insideLeft' }}
              />
              <RechartsTooltip 
                formatter={(value, name) => {
                  if (name === 'avgDevPhaseDays') {
                    return [`${value} days`, 'Dev Phase (Creation to Resolution)'];
                  } else if (name === 'avgQAPhaseDays') {
                    return [`${value} days`, 'QA Phase (Resolution to Closure)'];
                  }
                  return [value, name];
                }}
                labelFormatter={(label) => `Month: ${label}`}
              />
              <Legend 
                verticalAlign="top" 
                height={36}
                formatter={(value) => {
                  if (value === 'avgDevPhaseDays') {
                    return 'Dev Phase (Creation to Resolution)';
                  } else if (value === 'avgQAPhaseDays') {
                    return 'QA Phase (Resolution to Closure)';
                  }
                  return value;
                }}
              />
              <Bar 
                dataKey="avgDevPhaseDays" 
                name="avgDevPhaseDays" 
                stackId="a" 
                fill="#4472C4" 
                label={{ 
                  position: 'inside', 
                  fill: '#FFFFFF', 
                  fontSize: 10,
                  formatter: (value) => value > 3 ? value : ''
                }}
              />
              <Bar 
                dataKey="avgQAPhaseDays" 
                name="avgQAPhaseDays" 
                stackId="a" 
                fill="#ED7D31" 
                label={{ 
                  position: 'inside', 
                  fill: '#FFFFFF', 
                  fontSize: 10,
                  formatter: (value) => value > 3 ? value : ''
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    );
  };
  
  // Function to render daily P1+P2 bugs chart
  const renderDailyP1P2Bugs = () => {
    if (loadingDailyPriority) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (dailyPriorityError) {
      return (
        <Box>
          <Typography color="error">{dailyPriorityError}</Typography>
        </Box>
      );
    }
    
    // Filter out weekends if needed
    const filteredData = dailyPriorityBugs.filter(item => {
      const date = new Date(item.date);
      return date.getDay() !== 0 && date.getDay() !== 6; // Skip weekends (0 = Sunday, 6 = Saturday)
    });
    
    // Calculate total P1+P2 bugs
    const totalP1 = filteredData.reduce((sum, item) => sum + (item.P1?.total || 0), 0);
    const totalP2 = filteredData.reduce((sum, item) => sum + (item.P2?.total || 0), 0);
    const totalPriorityBugs = totalP1 + totalP2;
    
    // Environment colors for consistent visualization
    const environmentColors = {
      'Legacy': '#0070C0',           // Blue
      'Modernization': '#00B050',    // Green
      'Legacy and Modernization': '#FFC000', // Amber
      'Unknown': '#95A5A6'           // Grey
    };
    
    // Generate colors for environments
    const getEnvironmentColor = (environment, index) => {
      if (environmentColors[environment]) {
        return environmentColors[environment];
      }
      
      // Generate a color based on index if not predefined
      const colors = [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', 
        '#DDA0DD', '#98D8C8', '#F39C12', '#E74C3C', '#9B59B6',
        '#3498DB', '#1ABC9C', '#2ECC71', '#F1C40F', '#E67E22'
      ];
      return colors[index % colors.length];
    };
    
    // Prepare data for P1 chart
    const p1Data = filteredData.map(item => {
      const p1Item = { date: item.date };
      dailyPriorityEnvironments.forEach(env => {
        p1Item[env] = item.P1?.[env] || 0;
      });
      return p1Item;
    });
    
    // Prepare data for P2 chart
    const p2Data = filteredData.map(item => {
      const p2Item = { date: item.date };
      dailyPriorityEnvironments.forEach(env => {
        p2Item[env] = item.P2?.[env] || 0;
      });
      return p2Item;
    });
    
    return (
      <Box>
        <Typography variant="body1" paragraph>
          Daily P1+P2 bugs for the last 60 days (excluding weekends), stacked by environment.
        </Typography>
        <Typography variant="subtitle1" paragraph fontWeight="bold" color="primary">
          Total Bugs: {totalPriorityBugs} (P1: {totalP1}, P2: {totalP2})
        </Typography>
        <Typography variant="caption" paragraph>
          This chart uses real data from the database and shows bugs grouped by environment.
        </Typography>
        
        {/* P1 Bugs Chart */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" align="center" gutterBottom sx={{ color: '#FF0000' }}>
            P1 Bugs by Environment
          </Typography>
          <Box sx={{ height: 250, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={p1Data}
                margin={{
                  top: 20, right: 30, left: 20, bottom: 70,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  angle={-45} 
                  textAnchor="end" 
                  height={70} 
                  tick={{ fontSize: 10 }}
                />
                <YAxis 
                  label={{ value: 'Number of P1 Bugs', angle: -90, position: 'insideLeft' }}
                />
                <RechartsTooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
                      
                      return (
                        <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc' }}>
                          <p style={{ fontWeight: 'bold' }}>{`Date: ${label}`}</p>
                          {payload.map((entry, index) => (
                            <p key={index} style={{ color: entry.color }}>
                              {`${entry.dataKey}: ${entry.value} P1 bugs`}
                            </p>
                          ))}
                          <p style={{ fontWeight: 'bold' }}>{`Total P1: ${total} bugs`}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend verticalAlign="top" height={36} />
                
                {/* Render stacked bars for each environment */}
                {dailyPriorityEnvironments.map((environment, index) => (
                  <Bar 
                    key={environment}
                    dataKey={environment} 
                    name={environment} 
                    stackId="p1"
                    fill={getEnvironmentColor(environment, index)}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
        
        {/* P2 Bugs Chart */}
        <Box>
          <Typography variant="h6" align="center" gutterBottom sx={{ color: '#FFA500' }}>
            P2 Bugs by Environment
          </Typography>
          <Box sx={{ height: 250, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={p2Data}
                margin={{
                  top: 20, right: 30, left: 20, bottom: 70,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  angle={-45} 
                  textAnchor="end" 
                  height={70} 
                  tick={{ fontSize: 10 }}
                />
                <YAxis 
                  label={{ value: 'Number of P2 Bugs', angle: -90, position: 'insideLeft' }}
                />
                <RechartsTooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
                      
                      return (
                        <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc' }}>
                          <p style={{ fontWeight: 'bold' }}>{`Date: ${label}`}</p>
                          {payload.map((entry, index) => (
                            <p key={index} style={{ color: entry.color }}>
                              {`${entry.dataKey}: ${entry.value} P2 bugs`}
                            </p>
                          ))}
                          <p style={{ fontWeight: 'bold' }}>{`Total P2: ${total} bugs`}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend verticalAlign="top" height={36} />
                
                {/* Render stacked bars for each environment */}
                {dailyPriorityEnvironments.map((environment, index) => (
                  <Bar 
                    key={environment}
                    dataKey={environment} 
                    name={environment} 
                    stackId="p2"
                    fill={getEnvironmentColor(environment, index)}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Box>
    );
  };

  // Function to render daily P1/P2 created chart
  const renderDailyP1P2Created = () => {
    if (loadingDailyP1P2Created) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (dailyP1P2CreatedError) {
      return (
        <Box>
          <Typography color="error">{dailyP1P2CreatedError}</Typography>
        </Box>
      );
    }
    
    // Filter out weekends if needed
    const filteredData = dailyP1P2Created.filter(item => {
      const date = new Date(item.date);
      return date.getDay() !== 0 && date.getDay() !== 6; // Skip weekends (0 = Sunday, 6 = Saturday)
    });
    
    // Calculate totals
    const totalP1 = filteredData.reduce((sum, item) => sum + (item.P1?.total || 0), 0);
    const totalP2 = filteredData.reduce((sum, item) => sum + (item.P2?.total || 0), 0);
    const totalPriorityBugs = totalP1 + totalP2;
    
    // Environment colors for consistent visualization
    const environmentColors = {
      'Legacy': '#0070C0',           // Blue
      'Modernization': '#00B050',    // Green
      'Legacy and Modernization': '#FFC000', // Amber
      'Unknown': '#95A5A6'           // Grey
    };
    
    // Generate colors for environments
    const getEnvironmentColor = (environment, index) => {
      if (environmentColors[environment]) {
        return environmentColors[environment];
      }
      
      // Generate a color based on index if not predefined
      const colors = [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', 
        '#DDA0DD', '#98D8C8', '#F39C12', '#E74C3C', '#9B59B6',
        '#3498DB', '#1ABC9C', '#2ECC71', '#F1C40F', '#E67E22'
      ];
      return colors[index % colors.length];
    };
    
    // Prepare data for P1 chart
    const p1Data = filteredData.map(item => {
      const result = { date: item.date };
      dailyP1P2CreatedEnvironments.forEach(env => {
        result[env] = item.P1?.[env] || 0;
      });
      return result;
    });
    
    // Prepare data for P2 chart
    const p2Data = filteredData.map(item => {
      const result = { date: item.date };
      dailyP1P2CreatedEnvironments.forEach(env => {
        result[env] = item.P2?.[env] || 0;
      });
      return result;
    });
    
    return (
      <Box>
        <Typography variant="body1" paragraph>
          Last 60 days P1/P2 issues created each day (stacked bar chart).
        </Typography>
        <Typography variant="subtitle1" paragraph fontWeight="bold" color="primary">
          Total Issues: {totalPriorityBugs} (P1: {totalP1}, P2: {totalP2})
        </Typography>
        <Typography variant="caption" paragraph>
          This chart shows P1 and P2 priority issues created each day, grouped by environment, excluding weekends.
        </Typography>
        
        {/* P1 Issues Chart */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" align="center" gutterBottom sx={{ color: '#FF0000' }}>
            P1 Issues by Environment
          </Typography>
          <Box sx={{ height: 250, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={p1Data}
                margin={{
                  top: 20, right: 30, left: 20, bottom: 70,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  angle={-45} 
                  textAnchor="end" 
                  height={70} 
                  tick={{ fontSize: 10 }}
                />
                <YAxis 
                  label={{ value: 'Number of P1 Issues', angle: -90, position: 'insideLeft' }}
                />
                <RechartsTooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
                      
                      return (
                        <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc' }}>
                          <p style={{ fontWeight: 'bold' }}>{`Date: ${label}`}</p>
                          {payload.map((entry, index) => (
                            <p key={index} style={{ color: entry.color }}>
                              {`${entry.dataKey}: ${entry.value} P1 issues`}
                            </p>
                          ))}
                          <p style={{ fontWeight: 'bold' }}>{`Total P1: ${total} issues`}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend verticalAlign="top" height={36} />
                
                {/* Render stacked bars for each environment */}
                {dailyP1P2CreatedEnvironments.map((environment, index) => (
                  <Bar 
                    key={environment}
                    dataKey={environment} 
                    name={environment} 
                    stackId="p1"
                    fill={getEnvironmentColor(environment, index)}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
        
        {/* P2 Issues Chart */}
        <Box>
          <Typography variant="h6" align="center" gutterBottom sx={{ color: '#FFA500' }}>
            P2 Issues by Environment
          </Typography>
          <Box sx={{ height: 250, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={p2Data}
                margin={{
                  top: 20, right: 30, left: 20, bottom: 70,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  angle={-45} 
                  textAnchor="end" 
                  height={70} 
                  tick={{ fontSize: 10 }}
                />
                <YAxis 
                  label={{ value: 'Number of P2 Issues', angle: -90, position: 'insideLeft' }}
                />
                <RechartsTooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
                      
                      return (
                        <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc' }}>
                          <p style={{ fontWeight: 'bold' }}>{`Date: ${label}`}</p>
                          {payload.map((entry, index) => (
                            <p key={index} style={{ color: entry.color }}>
                              {`${entry.dataKey}: ${entry.value} P2 issues`}
                            </p>
                          ))}
                          <p style={{ fontWeight: 'bold' }}>{`Total P2: ${total} issues`}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend verticalAlign="top" height={36} />
                
                {/* Render stacked bars for each environment */}
                {dailyP1P2CreatedEnvironments.map((environment, index) => (
                  <Bar 
                    key={environment}
                    dataKey={environment} 
                    name={environment} 
                    stackId="p2"
                    fill={getEnvironmentColor(environment, index)}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Box>
    );
  };

  // Function to render daily incoming issues by environment chart
  const renderDailyIncomingByEnvironment = () => {
    if (loadingDailyIncomingByEnvironment) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (dailyIncomingByEnvironmentError) {
      return (
        <Box>
          <Typography color="error">{dailyIncomingByEnvironmentError}</Typography>
        </Box>
      );
    }
    
    // Filter out weekends if needed
    const filteredData = dailyIncomingByEnvironment.filter(item => {
      const date = new Date(item.date);
      return date.getDay() !== 0 && date.getDay() !== 6; // Skip weekends (0 = Sunday, 6 = Saturday)
    });
    
    // Calculate total bugs
    const totalBugs = filteredData.reduce((sum, item) => {
      let dayTotal = 0;
      incomingEnvironments.forEach(env => {
        dayTotal += item[env] || 0;
      });
      return sum + dayTotal;
    }, 0);
    
    // Environment colors for consistent visualization
    const environmentColors = {
      'Legacy': '#0070C0',           // Blue
      'Modernization': '#00B050',    // Green
      'Legacy and Modernization': '#FFC000', // Amber
      'Unknown': '#95A5A6'           // Grey
    };
    
    // Generate colors for environments
    const getEnvironmentColor = (environment, index) => {
      if (environmentColors[environment]) {
        return environmentColors[environment];
      }
      
      // Generate a color based on index if not predefined
      const colors = [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', 
        '#DDA0DD', '#98D8C8', '#F39C12', '#E74C3C', '#9B59B6',
        '#3498DB', '#1ABC9C', '#2ECC71', '#F1C40F', '#E67E22'
      ];
      return colors[index % colors.length];
    };
    
    return (
      <Box>
        <Typography variant="body1" paragraph>
          Last 60 days incoming issues grouped by Environment (stacked bar chart).
        </Typography>
        <Typography variant="subtitle1" paragraph fontWeight="bold" color="primary">
          Total Issues: {totalBugs}
        </Typography>
        <Typography variant="caption" paragraph>
          This chart shows all incoming issues created each day, grouped by environment, excluding weekends.
        </Typography>
        
        <Box sx={{ height: 400, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={filteredData}
              margin={{
                top: 20, right: 30, left: 20, bottom: 70,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                angle={-45} 
                textAnchor="end" 
                height={70} 
                tick={{ fontSize: 10 }}
              />
              <YAxis 
                label={{ value: 'Number of Issues', angle: -90, position: 'insideLeft' }}
              />
              <RechartsTooltip 
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
                    
                    return (
                      <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc' }}>
                        <p style={{ fontWeight: 'bold' }}>{`Date: ${label}`}</p>
                        {payload.map((entry, index) => (
                          <p key={index} style={{ color: entry.color }}>
                            {`${entry.dataKey}: ${entry.value} issues`}
                          </p>
                        ))}
                        <p style={{ fontWeight: 'bold' }}>{`Total: ${total} issues`}</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend verticalAlign="top" height={36} />
              
              {/* Render stacked bars for each environment */}
              {incomingEnvironments.map((environment, index) => (
                <Bar 
                  key={environment}
                  dataKey={environment} 
                  name={environment} 
                  stackId="a"
                  fill={getEnvironmentColor(environment, index)}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    );
  };

  // Function to handle sorting
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Function to sort data
  const sortData = (data, comparator) => {
    const stabilizedData = data.map((el, index) => [el, index]);
    stabilizedData.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedData.map((el) => el[0]);
  };

  // Comparator function for sorting
  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  // Descending comparator
  const descendingComparator = (a, b, orderBy) => {
    // Handle special cases for dates
    if (orderBy === 'createdDate') {
      const dateA = new Date(a[orderBy]);
      const dateB = new Date(b[orderBy]);
      if (dateB < dateA) return -1;
      if (dateB > dateA) return 1;
      return 0;
    }
    
    // Handle numeric ID
    if (orderBy === 'id') {
      const idA = parseInt(a[orderBy]);
      const idB = parseInt(b[orderBy]);
      if (idB < idA) return -1;
      if (idB > idA) return 1;
      return 0;
    }
    
    // Handle priority (P1 < P2 < P3 < P4)
    if (orderBy === 'priority') {
      const priorityOrder = { 'P1': 1, 'P2': 2, 'P3': 3, 'P4': 4 };
      const priorityA = priorityOrder[a[orderBy]] || 5;
      const priorityB = priorityOrder[b[orderBy]] || 5;
      if (priorityB < priorityA) return -1;
      if (priorityB > priorityA) return 1;
      return 0;
    }
    
    // Handle null/undefined values
    if (!b[orderBy]) return -1;
    if (!a[orderBy]) return 1;
    
    // Default string comparison
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  };

  // Render ticket details modal
  const renderTicketDetailsModal = () => {
    // Sort the bugs if a sort order is specified
    const sortedBugs = orderBy 
      ? sortData(selectedBugs, getComparator(order, orderBy))
      : selectedBugs;

    return (
      <Dialog 
        open={ticketDetailsOpen} 
        onClose={closeTicketDetails}
        maxWidth="xl"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '95%',
            maxWidth: '1600px',
          }
        }}
      >
        <DialogTitle>
          {ticketDetailsTitle}
          <IconButton
            aria-label="close"
            onClick={closeTicketDetails}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTickets.length === 0 ? (
            <Typography>No tickets found.</Typography>
          ) : (
            <>
              <Typography variant="subtitle1" gutterBottom>
                Showing {selectedBugs.length} of {selectedTickets.length} tickets
              </Typography>
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table stickyHeader aria-label="ticket details table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 50 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>
                        <TableSortLabel
                          active={orderBy === 'id'}
                          direction={orderBy === 'id' ? order : 'asc'}
                          onClick={() => handleRequestSort('id')}
                        >
                          ID
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 400 }}>
                        <TableSortLabel
                          active={orderBy === 'title'}
                          direction={orderBy === 'title' ? order : 'asc'}
                          onClick={() => handleRequestSort('title')}
                        >
                          Title
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>
                        <TableSortLabel
                          active={orderBy === 'state'}
                          direction={orderBy === 'state' ? order : 'asc'}
                          onClick={() => handleRequestSort('state')}
                        >
                          State
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>
                        <TableSortLabel
                          active={orderBy === 'priority'}
                          direction={orderBy === 'priority' ? order : 'asc'}
                          onClick={() => handleRequestSort('priority')}
                        >
                          Priority
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 110 }}>
                        <TableSortLabel
                          active={orderBy === 'createdDate'}
                          direction={orderBy === 'createdDate' ? order : 'asc'}
                          onClick={() => handleRequestSort('createdDate')}
                        >
                          Created Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>
                        <TableSortLabel
                          active={orderBy === 'team'}
                          direction={orderBy === 'team' ? order : 'asc'}
                          onClick={() => handleRequestSort('team')}
                        >
                          Team
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>
                        <TableSortLabel
                          active={orderBy === 'iterationPath'}
                          direction={orderBy === 'iterationPath' ? order : 'asc'}
                          onClick={() => handleRequestSort('iterationPath')}
                        >
                          Iteration Path
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedBugs.map((bug, index) => (
                      <TableRow key={bug.id} hover>
                        <TableCell sx={{ fontWeight: 'bold' }}>{index + 1}</TableCell>
                        <TableCell>
                          <a 
                            href={`https://dev.azure.com/tr-legal/HighQ%20Tech/_workitems/edit/${bug.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              textDecoration: 'none',
                              color: '#0078d4'
                            }}
                          >
                            {bug.id}
                          </a>
                        </TableCell>
                        <TableCell>{bug.title}</TableCell>
                        <TableCell>{bug.state}</TableCell>
                        <TableCell>{bug.priority}</TableCell>
                        <TableCell>{new Date(bug.createdDate).toLocaleDateString()}</TableCell>
                        <TableCell>{bug.team}</TableCell>
                        <TableCell>{bug.iterationPath || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {selectedBugs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <CircularProgress size={24} sx={{ mr: 1 }} />
                          Loading ticket details...
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTicketDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box mb={3}>
        <Typography variant="body1" color="textSecondary">
          Production bug trends and statistics
        </Typography>
      </Box>
      
      {renderTrendInsights()}
      
      <Grid container spacing={3}>
        {/* New Average Days Open Info Graphic - Moved to Top */}
        <Grid item xs={12}>
          <AverageDaysOpenInfo />
        </Grid>

        <Grid item xs={12}>
          {renderTrendCard('Monthly Created vs Closed', renderMonthlyCreatedVsClosed())}
        </Grid>
        
        {/* Resolution Time Charts - Side by Side */}
        <Grid item xs={12} md={6}>
          <AverageResolutionTimeChart />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <AverageResolutionTimeByCreatedMonthChart />
        </Grid>
        
        <Grid item xs={12} md={6}>
          {renderTrendCard('Last 60 days P1/P2 Issues Created', renderDailyP1P2Created())}
        </Grid>
        
        <Grid item xs={12} md={6}>
          {renderTrendCard('Last 60 days Incoming Issues by Environment', renderDailyIncomingByEnvironment())}
        </Grid>
        
      </Grid>
      
      {/* Render ticket details modal */}
      {renderTicketDetailsModal()}
    </Box>
  );
};

export default BugTrendsView;
